
import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';

interface DataCounts {
  users: number;
  products: number;
  orders: number;
  partners: number;
}

export default function AdminPage() {
  const [dataCounts, setDataCounts] = useState<DataCounts>({
    users: 0,
    products: 0,
    orders: 0,
    partners: 0
  });
  const [loading, setLoading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const checkDataCounts = async () => {
    setLoading(true);
    try {
      const tables = ['users', 'products', 'orders', 'partners'];
      const counts: any = {};

      for (const table of tables) {
        const { count, error } = await supabase
          .from(table)
          .select('*', { count: 'exact', head: true });
        
        if (error) {
          console.error(`Error counting ${table}:`, error);
          counts[table] = 0;
        } else {
          counts[table] = count || 0;
        }
      }

      setDataCounts(counts);
    } catch (error) {
      console.error('Error checking data counts:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearAllData = async () => {
    if (confirmText !== 'XOA TAT CA') {
      alert('Vui lòng nhập chính xác "XOA TAT CA" để xác nhận');
      return;
    }

    setClearing(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Không có quyền truy cập');
      }

      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/clear-data`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (result.success) {
        alert('✅ Đã xóa toàn bộ dữ liệu thành công!\n\nDữ liệu sản phẩm được giữ nguyên.');
        setShowConfirm(false);
        setConfirmText('');
        await checkDataCounts();
      } else {
        throw new Error(result.error || 'Có lỗi xảy ra');
      }
    } catch (error: any) {
      console.error('Error clearing data:', error);
      alert('❌ Lỗi: ' + error.message);
    } finally {
      setClearing(false);
    }
  };

  useEffect(() => {
    checkDataCounts();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-black mb-2">Quản Trị Hệ Thống</h1>
          <p className="text-gray-600">Quản lý và đồng bộ dữ liệu với Supabase</p>
        </div>

        {/* Data Status Card */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-black flex items-center">
              <i className="ri-database-2-line text-orange-500 mr-2"></i>
              Trạng Thái Dữ Liệu
            </h2>
            <button
              onClick={checkDataCounts}
              disabled={loading}
              className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 flex items-center"
            >
              <i className={`ri-refresh-line mr-2 ${loading ? 'animate-spin' : ''}`}></i>
              {loading ? 'Đang kiểm tra...' : 'Kiểm Tra'}
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-black">{dataCounts.users}</div>
              <div className="text-sm text-gray-600">Người dùng</div>
            </div>
            <div className="text-center p-4 bg-orange-50 rounded-lg border border-orange-200">
              <div className="text-2xl font-bold text-orange-600">{dataCounts.products}</div>
              <div className="text-sm text-orange-600 font-medium">Sản phẩm (Giữ nguyên)</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-black">{dataCounts.orders}</div>
              <div className="text-sm text-gray-600">Đơn hàng</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-black">{dataCounts.partners}</div>
              <div className="text-sm text-gray-600">Đối tác</div>
            </div>
          </div>
        </div>

        {/* Clear Data Section */}
        <div className="bg-white border border-red-200 rounded-lg p-6 mb-6 shadow-sm">
          <h2 className="text-xl font-semibold text-red-600 flex items-center mb-4">
            <i className="ri-delete-bin-line mr-2"></i>
            Xóa Toàn Bộ Dữ Liệu
          </h2>
          
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-start">
              <i className="ri-alert-line text-red-500 mt-1 mr-2"></i>
              <div>
                <p className="text-red-800 font-medium mb-2">⚠️ Cảnh báo quan trọng:</p>
                <ul className="text-red-700 text-sm space-y-1">
                  <li>• Sẽ xóa toàn bộ: Người dùng, Đơn hàng, Đối tác</li>
                  <li>• <strong>Dữ liệu sản phẩm sẽ được giữ nguyên</strong></li>
                  <li>• Thao tác này không thể hoàn tác</li>
                  <li>• Hệ thống sẽ được reset về trạng thái ban đầu</li>
                </ul>
              </div>
            </div>
          </div>

          <button
            onClick={() => setShowConfirm(true)}
            className="w-full px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center justify-center"
          >
            <i className="ri-delete-bin-line mr-2"></i>
            Xóa Toàn Bộ Dữ Liệu
          </button>
        </div>

        {/* Quick Links */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <h2 className="text-xl font-semibold text-black flex items-center mb-4">
            <i className="ri-links-line text-orange-500 mr-2"></i>
            Liên Kết Nhanh
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <a
              href="/users"
              className="p-4 border border-gray-200 rounded-lg hover:border-orange-300 hover:bg-orange-50 transition-colors text-center group"
            >
              <i className="ri-user-line text-2xl text-gray-600 group-hover:text-orange-500 mb-2 block"></i>
              <div className="text-sm font-medium text-black">Người dùng</div>
            </a>
            <a
              href="/products"
              className="p-4 border border-gray-200 rounded-lg hover:border-orange-300 hover:bg-orange-50 transition-colors text-center group"
            >
              <i className="ri-product-hunt-line text-2xl text-gray-600 group-hover:text-orange-500 mb-2 block"></i>
              <div className="text-sm font-medium text-black">Sản phẩm</div>
            </a>
            <a
              href="/orders"
              className="p-4 border border-gray-200 rounded-lg hover:border-orange-300 hover:bg-orange-50 transition-colors text-center group"
            >
              <i className="ri-shopping-cart-line text-2xl text-gray-600 group-hover:text-orange-500 mb-2 block"></i>
              <div className="text-sm font-medium text-black">Đơn hàng</div>
            </a>
            <a
              href="/partners"
              className="p-4 border border-gray-200 rounded-lg hover:border-orange-300 hover:bg-orange-50 transition-colors text-center group"
            >
              <i className="ri-team-line text-2xl text-gray-600 group-hover:text-orange-500 mb-2 block"></i>
              <div className="text-sm font-medium text-black">Đối tác</div>
            </a>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="text-center mb-6">
              <i className="ri-alert-line text-red-500 text-4xl mb-4"></i>
              <h3 className="text-xl font-bold text-black mb-2">Xác Nhận Xóa Dữ Liệu</h3>
              <p className="text-gray-600 mb-4">
                Để xác nhận xóa toàn bộ dữ liệu, vui lòng nhập chính xác:
              </p>
              <div className="bg-gray-100 p-3 rounded-lg mb-4">
                <code className="text-red-600 font-bold">XOA TAT CA</code>
              </div>
            </div>

            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Nhập XOA TAT CA để xác nhận"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 mb-4"
            />

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowConfirm(false);
                  setConfirmText('');
                }}
                disabled={clearing}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Hủy
              </button>
              <button
                onClick={clearAllData}
                disabled={clearing || confirmText !== 'XOA TAT CA'}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center"
              >
                {clearing ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Đang xóa...
                  </>
                ) : (
                  <>
                    <i className="ri-delete-bin-line mr-2"></i>
                    Xóa Ngay
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
